import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import RouteFallback from './components/RouteFallback';
// LandingPage is the critical initial route — keep it eager so the
// competitions list renders without waiting on a second network round-trip.
import LandingPage from './pages/LandingPage';

// Everything below is route-based code-split via React.lazy. Each lazy import
// becomes its own chunk that is only fetched when the user navigates to it,
// keeping the initial bundle focused on the landing/competitions experience.
const CompetitionDetailPage = lazy(() => import('./pages/CompetitionDetailPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const WinnersPage = lazy(() => import('./pages/WinnersPage'));
const AdminCompetitionsPage = lazy(() => import('./pages/admin/AdminCompetitionsPage'));
const AdminCompetitionFormPage = lazy(() => import('./pages/admin/AdminCompetitionFormPage'));
const AdminCompetitionDetailPage = lazy(() => import('./pages/admin/AdminCompetitionDetailPage'));
const AdminCompetitionFlyersPage = lazy(() => import('./pages/admin/AdminCompetitionFlyersPage'));
const AdminJudgingPage = lazy(() => import('./pages/admin/AdminJudgingPage'));
const AdminLoyaltyStatsPage = lazy(() => import('./pages/admin/AdminLoyaltyStatsPage'));
const AdminProposalsPage = lazy(() => import('./pages/admin/AdminProposalsPage'));
const AdminWithdrawalsPage = lazy(() => import('./pages/admin/AdminWithdrawalsPage'));
const AdminPartnersPage = lazy(() => import('./pages/admin/AdminPartnersPage'));
const AdminShareScreenshotsPage = lazy(() => import('./pages/admin/AdminShareScreenshotsPage'));
const LoyaltyDashboardPage = lazy(() => import('./pages/LoyaltyDashboardPage'));
const WithdrawPage = lazy(() => import('./pages/WithdrawPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const PartnerTrainingPage = lazy(() => import('./pages/PartnerTrainingPage'));
const PartnerToolkitPage = lazy(() => import('./pages/PartnerToolkitPage'));
const PartnerQuizPage = lazy(() => import('./pages/PartnerQuizPage'));
const PartnersLandingPage = lazy(() => import('./pages/PartnersLandingPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const BecomeAPartnerPage = lazy(() => import('./pages/BecomeAPartnerPage'));
const WinSlugPage = lazy(() => import('./pages/WinSlugPage'));
const StudentVillagePage = lazy(() => import('./pages/StudentVillagePage'));

function App() {
  // Capture ?ref= referral code from URL into sessionStorage so any landing page
  // deep link (e.g. prizes.my-investments.io/?ref=ABC123) carries the code forward
  // to the registration form, even if the user navigates before registering.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      sessionStorage.setItem('pending_referral_code', ref);
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            {/* Competitions merged into the landing page — redirect the old route. */}
            <Route path="/competitions" element={<Navigate to="/" replace />} />
            <Route path="/competitions/:id" element={<CompetitionDetailPage />} />
            {/* Friendly public URL /win/<slug> → resolves to the competition. */}
            <Route path="/win/:slug" element={<WinSlugPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            {/* mktg-student-village: themed landing grouping student-need competitions. */}
            <Route path="/student-village" element={<StudentVillagePage />} />
            {/* hotfix-1-169: light influencer-partner info page — the intermediary
                the old "Partners" nav link used to skip straight past to /register. */}
            <Route path="/become-a-partner" element={<BecomeAPartnerPage />} />
            <Route path="/winners" element={<WinnersPage />} />
            {/* corp-1-5: corporate "Partner with us / Sponsor a prize" link-out page */}
            <Route path="/partners" element={<PartnersLandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/admin" element={<Navigate to="/admin/competitions" replace />} />
            <Route path="/admin/competitions" element={<AdminCompetitionsPage />} />
            <Route path="/admin/competitions/new" element={<AdminCompetitionFormPage />} />
            <Route path="/admin/competitions/:id" element={<AdminCompetitionDetailPage />} />
            <Route path="/admin/competitions/:id/edit" element={<AdminCompetitionFormPage />} />
            <Route path="/admin/competitions/:id/flyers" element={<AdminCompetitionFlyersPage />} />
            <Route path="/admin/competitions/:id/judging" element={<AdminJudgingPage />} />
            <Route path="/admin/proposals" element={<AdminProposalsPage />} />
            <Route path="/admin/loyalty" element={<AdminLoyaltyStatsPage />} />
            <Route path="/admin/withdrawals" element={<AdminWithdrawalsPage />} />
            <Route path="/admin/partners" element={<AdminPartnersPage />} />
            <Route path="/admin/share-screenshots" element={<AdminShareScreenshotsPage />} />
            <Route path="/dashboard/loyalty" element={<LoyaltyDashboardPage />} />
            <Route path="/dashboard/withdraw" element={<WithdrawPage />} />
            <Route path="/dashboard/partner" element={<PartnerPage />} />
            <Route path="/dashboard/partner/training" element={<PartnerTrainingPage />} />
            <Route path="/dashboard/partner/toolkit" element={<PartnerToolkitPage />} />
            <Route path="/dashboard/partner/quiz" element={<PartnerQuizPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
